/* eslint-disable @typescript-eslint/await-thenable */
import ssf from '@elliemae/em-ssf-guest';
import { TooltipTextProvider } from '@elliemae/ds-truncated-tooltip-text';
import {
  useLookupOptions,
  deriveAccordionFieldMapFromSchema,
  applyClientLookupsToSchema,
  useRawLookupOptions,
  useLoanDataFieldSelectorHook,
  AdjustmentsProvider,
  validateAndSubmitSearchForm,
  expandAccordionsByErrorPaths,
} from '@frontend/pricing-search';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import {
  dispatchOriginData,
  dispatchUserData,
  getInitData,
  getSearchFormSchema,
  initTechOpsData,
  IS_USER_CANNOT_BE_FOUND,
  IS_DISABLED_USER,
  IS_INVALID_CREDENTIAL,
  IS_INVALID_CREDS,
  IS_INVALID_TOKEN,
  IS_RECONCILE,
  setMockInitData,
  setSearchConfiguration,
} from '../common/app-helpers';
import Common from '../common/services/Common';
import { initSchemaForm } from '../common/services/searchFormServices';
import Session, {
  ALL_SUMMARY_PARAMS,
  APPLICATION_NAME,
  IS_BUYSIDE,
  IS_DEBUG,
  IS_JWT_TOKEN,
  IS_LO,
  IS_LOCK_EXTENSION,
  IS_SCENARIO_MODE,
  IS_SELLSIDE,
  IS_STANDALONE,
  IS_VALIDATE,
  ORIGIN_TRANSACTION_TYPE,
  PRESERVED_NON_QM_DOC_LEVEL,
  PRICING_ID,
} from '../common/services/Session';
import { buysideRequested } from '../common/utils/constants/ErrorCodes';
import { startLog } from '../common/utils/logger';
import { openGlobalErrorModalAction } from '../data/globalErrorModal/actionCreators';
import { ERROR_MESSAGES } from '../common/utils/constants/ErrorMessages';
import {
  useLoanInformationSelector,
  usePricingSearchConfiguration,
  usePricingSearchConfigurations,
  useUserRolesSelector,
} from '../data/useSelectors';
import { userRolesActions } from '../store/userRoles';
import { lookupsActions } from '../store/lookups/index';
import { LockConfirmWrapper } from '../view/LockConfirm/LockConfirmWrapper';
import { LockExtension } from '../view/LockExtension/LockExtension';
import { LockSummary } from '../view/LockSummary/LockSummary';
import { Login } from '../view/Login';
import { SearchForm } from '../view/SearchForm/SearchForm';
import { useExpandAccordionsOnError } from '../view/components/Form/utils/customHooks';
import { SearchResult } from '../view/SearchResult';
import {
  ScenarioWrapper,
  ScenarioChannelSelect,
  ScenarioSearch,
  ScenarioResults,
  ScenarioSummary,
} from '../view/Scenario';
import {
  DebugWrapper,
  DebugSearch,
  DebugResults,
  DebugSummary,
} from '../view/Debug';
import { GlobalErrorModal } from '../view/components/GlobalErrorModal';
import { Header } from '../view/components/Header/index';
import { ScreenLoader } from '../view/components/ScreenLoader';
import {
  StyledEppsContainerBody,
  StyledEppsContainerHeader,
  StyledEppsParentContainer,
} from '../view/global/styles/styled';
import {
  PATH_LOCK_EXTENSION,
  PATH_LOCK_SUMMARY,
  PATH_LOGIN,
  PATH_SEARCH_FOR_PRODUCTS,
  PATH_SEARCH_RESULTS,
  PATH_SCENARIO,
  PATH_SCENARIO_CHANNEL,
  PATH_DEBUG,
  PATH_DEBUG_RESULTS,
} from './constants';
import { setLoanDataValue } from '../view/components/Form/utils/common';
import { getZeroBasedPricingSelector } from '../data/lockSummary/actionCreators';
import { useIsZeroBasedPricing } from '../common/utils/customHooks/useIsZeroBasedPricing';
import api from '../common/utils/api';
import { useSmartClientLoanUpdate } from '../common/services/useSmartClientLoanUpdate';
import { closeLoader, openLoader } from '../data/screenLoader/actions';
import { searchFormActions } from '../store/searchForm/index';

const App: React.FC = () => {
  const [hasInitSearchForm, setHasInitSearchForm] = useState(false);
  const [moveNext, setMoveNext] = useState(false);
  const [isError, setIsError] = useState(false);
  const [initData, setInitData] = useState(undefined);
  const [isTechOpsDataReady, setIsTechOpsDataReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false); // State to track login status
  const { roleName, canAssignLoanOfficer, viewDeliveryAndCommitment } =
    useUserRolesSelector();
  const navigate = useNavigate();
  const isBuyside = Session.get(IS_BUYSIDE);
  const isSellside = Session.get(IS_SELLSIDE);
  const isValidate = Session.get(IS_VALIDATE);
  const isJWTToken = Session.get(IS_JWT_TOKEN);
  const isScenarioMode = Session.get(IS_SCENARIO_MODE);
  const isDebugMode = Session.get(IS_DEBUG);
  const isTPO = Session.get(APPLICATION_NAME) === 'TPO';
  const loanData = useLoanDataFieldSelectorHook() as any;
  const {
    triggerRefreshPartnerUi,
    refreshUI,
    subscribeToLoanModalChange,
    unsubscribeLoanModalChange,
  } = useSmartClientLoanUpdate();
  const isSmartClient = Session.get(APPLICATION_NAME) === 'ENC';

  const isLO = Session.get(IS_LO);
  const dispatch = useDispatch();
  const rawGovtUpfrontFees = useLookupOptions('fee');
  const loanTermsLookups = useLookupOptions('loanTerms');
  const govtUpfrontFees = useMemo(
    () => rawGovtUpfrontFees || [],
    [rawGovtUpfrontFees],
  );
  const customFields = useLookupOptions('customFields');
  const rawDocumentationTypes = useLookupOptions('documentationTypes');
  const documentationTypes = useMemo(
    () => rawDocumentationTypes || [],
    [rawDocumentationTypes],
  );
  const isZeroBasedPricing = useIsZeroBasedPricing();

  const fetchData = async (isRefresh = false) => {
    const data = await getInitData(
      dispatch,
      isRefresh ?? false,
      setIsError,
      undefined,
    );
    if (data?.originData?.loanData?.scenarioTypeID) {
      Session.set(IS_SCENARIO_MODE, true);
      // setMoveNext(true);
    } else {
      Session.set(IS_SCENARIO_MODE, false);
    }
    const categories = await api.getAttributionCategories();
    data.attributionCategories = categories?.attributionCategoryResponses;
    if (data.code === undefined) {
      dispatchUserData(dispatch);
      dispatch(userRolesActions.fetchUserClientRoles());
      dispatch(lookupsActions.getLookupsDataAction({ dispatch } as any));
    }
    dispatch(
      getZeroBasedPricingSelector(
        data?.originData?.originalLoanInformation?.channel,
      ) as any,
    );
    setInitData(data);
  };

  useEffect(() => {
    refreshPartnerUi();
  }, [triggerRefreshPartnerUi]);

  const refreshPartnerUi = async () => {
    startLog(buysideRequested);
    Session.set(ALL_SUMMARY_PARAMS, {});
    Session.remove(IS_SCENARIO_MODE);
    // Preserved doc level only bridges in-app reloads (side switch, loan commit);
    // a fresh launch must not inherit one left in sessionStorage by a prior loan.
    if (triggerRefreshPartnerUi === 0) {
      Session.remove(PRESERVED_NON_QM_DOC_LEVEL);
    }

    if (!Session.get(IS_STANDALONE)) {
      fetchData(triggerRefreshPartnerUi > 0)
        .then(() => {
          dispatch(closeLoader());
          if (triggerRefreshPartnerUi > 0) {
            setMoveNext(false);
          }
        })
        .catch(() => {
          dispatch(closeLoader());
        });
    } else if (Session.get(IS_DEBUG) && Session.get(PRICING_ID)) {
      // Tech Ops: resolve the Pricing ID to real loan data via EPPS-100502,
      // authed with the opaque token from the deep link.
      dispatch(openLoader('Loading'));
      initTechOpsData(dispatch)
        .then(() => {
          dispatch(lookupsActions.getLookupsDataAction({ dispatch } as any));
          setIsTechOpsDataReady(true);
        })
        .catch(() => {
          // initTechOpsData already surfaced the error modal. Close the loader
          // but do NOT advance — leaving the user on an empty page.
          dispatch(closeLoader());
        });
    } else {
      // TODO(ngen): Might need to do more work to support this mock data flow
      setMockInitData(dispatch);
      setMoveNext(true);
    }
  };

  useEffect(() => {
    if (isSmartClient) {
      subscribeToLoanModalChange(() => {
        refreshUI();
      });
    }
    // Unsubscribe the listener on unmount
    return () => {
      if (isSmartClient) {
        unsubscribeLoanModalChange();
      }
    };
  }, [isSmartClient]);

  useEffect(() => {
    if (isError) {
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }) as any,
      );
    }
  }, [isError]);

  useEffect(() => {
    if (initData) {
      const {
        notTokenCredResource,
        errorLoanAmountBlank,
        hasLoanAmount,
        hasBorrowers,
        loanAmountBlank,
        lienPosition,
        originData,
        code,
        isRedirectToError,
        isHELOCLoan,
        hasHELOCInitialDraw,
        attributionCategories,
      } = initData;
      if (!notTokenCredResource) {
        if (!hasBorrowers && !isScenarioMode) {
          dispatch(
            openGlobalErrorModalAction({
              title: ERROR_MESSAGES.EPPS1044.title,
              errorMessage: ERROR_MESSAGES.EPPS1044.errorMessage,
              onCloseCallback: () => Common.closeApplication(),
              confirmLabel: 'Close ICE PPE',
              showClose: false,
            }) as any,
          );
        } else if (
          (lienPosition && govtUpfrontFees.length > 0) ||
          isScenarioMode
        ) {
          dispatchOriginData(
            dispatch,
            originData,
            govtUpfrontFees,
            customFields,
            isZeroBasedPricing,
            attributionCategories,
          );
          setMoveNext(true);
        }
      }
      if (initData[IS_RECONCILE] === IS_RECONCILE) {
        fetchData(true);
      }
      if (isRedirectToError) {
        setMoveNext(true);
        setIsError(true);
      }
      if (
        code &&
        code !== IS_INVALID_TOKEN &&
        code !== IS_INVALID_CREDENTIAL &&
        code !== IS_INVALID_CREDS &&
        code !== IS_DISABLED_USER &&
        code !== IS_USER_CANNOT_BE_FOUND
      ) {
        throw new Error();
      }
      // add custom fields to schema
    }
  }, [initData, govtUpfrontFees, isZeroBasedPricing]);

  useEffect(() => {
    const handleSearchValidation = async () => {
      const result = await validateAndSubmitSearchForm({
        schema: schemaWithLookups,
        schemaForm,
        preview: false,
        loanTerms: loanTermsLookups,
      });

      if (result.valid) {
        navigate(PATH_SEARCH_RESULTS);
      } else {
        dispatch(searchFormActions.setBannerIsOpen(true));
        if (result.errorPaths?.length) {
          const toOpen = expandAccordionsByErrorPaths(
            accordionFieldMap,
            result.errorPaths,
          );
          setActiveAccordions((prev) => ({ ...prev, ...toOpen }));
        }
        navigate(PATH_SEARCH_FOR_PRODUCTS);
      }
    };
    if (moveNext) {
      // Check if in Scenario mode and redirect to Scenario flow
      if (Session.get(IS_SCENARIO_MODE)) {
        navigate(PATH_SCENARIO_CHANNEL);
      } else if (Session.get(IS_DEBUG)) {
        // Tech Ops debug flow: skip login and the search form, land on results.
        // The Pricing ID loan is already priced, so there is nothing to fill in;
        // SearchResult qualifies from the store on mount.
        navigate(PATH_DEBUG_RESULTS);
      } else if (!isLO && !isJWTToken) {
        navigate(PATH_LOGIN);
      } else if (Session.get(IS_LOCK_EXTENSION)) {
        navigate(PATH_LOCK_EXTENSION);
      } else if (Session.get(IS_BUYSIDE) || Session.get(IS_SELLSIDE)) {
        navigate(PATH_LOCK_SUMMARY);
      } else {
        handleSearchValidation();
      }
    }
  }, [moveNext]);

  // Callback function to handle successful login
  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    navigate('/search-results');
  };

  const allLookups = useRawLookupOptions();
  const loanInfo = (useLoanInformationSelector() || {}) as any;
  const schema = usePricingSearchConfiguration();
  const searchConfigurations = usePricingSearchConfigurations();
  const schemaWithLookups = useMemo(() => {
    if (!schema) return schema;
    return applyClientLookupsToSchema(schema, allLookups);
  }, [schema, allLookups]);

  const hasLookups = Object.keys(allLookups || {}).length > 0;

  // Built here rather than in initTechOpsData: the no-channel fallback needs the
  // customFields lookup, which lands on setLookupsSuccess - a dispatch later than
  // the setLookupsRaw one that flips hasLookups, hence customFields as a dep.
  useEffect(() => {
    if (!isTechOpsDataReady || !loanInfo?.loanInformation) return;
    dispatch(
      setSearchConfiguration(
        getSearchFormSchema(loanInfo, searchConfigurations ?? [], customFields),
      ),
    );
  }, [isTechOpsDataReady, searchConfigurations, customFields]);

  // debug, mirroring the govtUpfrontFees gate the EPC path uses above.
  // Navigating before the lookups saga lands makes SearchResult restart it, and
  // its closeLoader then kills the rates loader mid-call.
  useEffect(() => {
    if (!isTechOpsDataReady || !hasLookups) return;
    dispatch(openLoader('Qualifying Programs'));
    setMoveNext(true);
  }, [isTechOpsDataReady, hasLookups]);

  const schemaForm = useForm<any>({
    mode: 'onSubmit',
    defaultValues: {},
    resetOptions: { keepValues: false },
  });

  const [activeAccordions, setActiveAccordions] = useState<
    Record<number, boolean>
  >({});

  const accordionFieldMap = useMemo(
    () => deriveAccordionFieldMapFromSchema(schemaWithLookups),
    [schemaWithLookups],
  );

  const { handleSearchFormInitialized } = useExpandAccordionsOnError({
    formReady: hasInitSearchForm,
    schemaWithLookups,
    schemaForm,
    accordionFieldMap,
    setActiveAccordions,
    loanTermsLookups,
  });

  useEffect(() => {
    if (isScenarioMode) return;
    if (loanInfo && roleName && Object.keys(loanInfo).length > 0) {
      initSchemaForm(schemaForm, {
        loanInfo,
        roleName,
        allLookups,
        documentationTypes,
        canAssignLoanOfficer,
        viewDeliveryAndCommitment,
      });
      setHasInitSearchForm(true);
    }
  }, [
    schemaForm,
    loanInfo,
    roleName,
    allLookups,
    documentationTypes,
    canAssignLoanOfficer,
    viewDeliveryAndCommitment,
  ]);

  const renderContent = useMemo(() => {
    return (
      <TooltipTextProvider>
        <StyledEppsContainerBody>
          {moveNext && (
            <AdjustmentsProvider>
              <Routes>
                <Route path={PATH_SCENARIO} element={<ScenarioWrapper />}>
                  <Route path="Channel" element={<ScenarioChannelSelect />} />
                  <Route
                    path="Search"
                    element={<ScenarioSearch schemaForm={schemaForm} />}
                  />
                  <Route path="Results" element={<ScenarioResults />} />
                  <Route path="Summary" element={<ScenarioSummary />} />
                </Route>
                <Route path={PATH_DEBUG} element={<DebugWrapper />}>
                  <Route
                    path="search"
                    element={<DebugSearch schemaForm={schemaForm} />}
                  />
                  <Route path="results" element={<DebugResults />} />
                  <Route path="summary" element={<DebugSummary />} />
                </Route>
                <Route
                  path={PATH_SEARCH_FOR_PRODUCTS}
                  element={
                    !hasInitSearchForm ? null : (
                      <SearchForm
                        schema={schemaWithLookups}
                        schemaForm={schemaForm}
                        activeAccordions={activeAccordions}
                        setActiveAccordions={setActiveAccordions}
                        accordionFieldMap={accordionFieldMap}
                        onInitialized={handleSearchFormInitialized}
                      />
                    )
                  }
                />
                <Route
                  path={PATH_LOCK_SUMMARY}
                  element={
                    isBuyside || isSellside ? (
                      <LockConfirmWrapper />
                    ) : (
                      <LockSummary />
                    )
                  }
                />
                <Route path={PATH_LOCK_EXTENSION} element={<LockExtension />} />
                {/* Login Route */}
                <Route
                  path="/login"
                  element={<Login onLoginSuccess={handleLoginSuccess} />}
                />

                {/* Search Results Route */}
                <Route path="/search-results" element={<SearchResult />} />
              </Routes>
            </AdjustmentsProvider>
          )}
        </StyledEppsContainerBody>
      </TooltipTextProvider>
    );
  }, [isLoggedIn, moveNext, activeAccordions, isBuyside, isSellside]);

  const Landing = (Content: JSX.Element) => {
    return (
      <>
        <StyledEppsParentContainer>
          {!isScenarioMode && !isDebugMode && (
            <StyledEppsContainerHeader>
              <Header
                schema={schemaWithLookups}
                schemaForm={schemaForm}
                setActiveAccordions={setActiveAccordions}
                accordionFieldMap={accordionFieldMap}
              />
            </StyledEppsContainerHeader>
          )}
          {Content}
        </StyledEppsParentContainer>
        <ScreenLoader />
        <GlobalErrorModal />
      </>
    );
  };

  return Landing(renderContent);
};

export default App;
